import React, {
  ComponentPropsWithoutRef,
  ReactElement,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import CompanyCard from './CompanyCard';
import AutoComplete from 'react-google-autocomplete';
import LocationIcon from '../../assets/icons/LocationIcon';
import useTheme from '../../hooks/ui/useTheme';
import CloseIcon from '../../assets/icons/actions/CloseIcon';
import { GooglePlaceResource } from '../../services/resources/GooglePlaceResource';
import { CompanyDetailsInterface, Franchise } from '../../services/exports/Interfaces';
import collect from 'collect.js';
import { useLocationData } from './provider/LocationDataProvider';
import DeliveryAddressForm from './form/DeliveryAddressForm';
import { ORDER_METHODS } from '../../services/exports/Constants';
import useDeliveryCompatibleStuff from './useDeliveryCompatibleStuff';
import LoadingIndicator from '../../components-library/loading/LoadingIndicator';
import { useFormContext } from 'react-hook-form';
import { DeliveryAddressSchema } from '../../modules/mobile/modals/delivery-address/form/schema';
import InfoIcon from '../../assets/logo/InfoIcon';
import classnames from 'classnames';
import useGoogleMaps from '../../hooks/location/useGoogleMaps';
import { ValidationErrors } from './types';
import { Loader2, LocateFixed } from 'lucide-react';
import useAlert from '../../hooks/utility/useAlert';
import NoCouriersErrorContent from '../../components-library/delivery/NoCouriersErrorContent';

export interface CompanyDistanceInterface {
  company: CompanyDetailsInterface;
  distance?: number;
}

interface Props extends ComponentPropsWithoutRef<'section'> {
  data: Franchise;
  addressSearch: string;
  locationSwitchDisabled: boolean;
  setAddressSearch: (value: string) => void;
  validationError?: ValidationErrors;
}

/**
 * Module scope on purpose: the label can contain the interactive promo-code button, and a component
 * redefined inside CompaniesList would remount that button on every keystroke in the address field
 * (the address form is watched unfiltered), stealing focus from it.
 */
function ErrorLabel({ message }: { message: ReactNode }): ReactElement {
  return (
    <p className={errorLabel} data-test="order-method-error-label">
      <InfoIcon className="h-6 w-6 shrink-0" color="#BC4C6B" />
      <span>{message}</span>
    </p>
  );
}

export default function CompaniesList(props: Props) {
  const {
    data,
    addressSearch,
    className,
    locationSwitchDisabled,
    setAddressSearch,
    validationError: addressValidationError,
    ...rest
  } = props;
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:CompaniesList' });
  const { t: tErrors } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:OrderMethodModal' });
  const deliveryAddressForm = useFormContext<DeliveryAddressSchema>();
  const { orderMethod, validationError, masterPromoCode, resetError, setCompany, company } = useLocationData();
  const { isSubmitSuccessful, isSubmitting } = deliveryAddressForm.formState;
  const theme = useTheme();
  const alert = useAlert();
  const [place, setPlace] = useState<GooglePlaceResource>(null);
  const [locating, setLocating] = useState<boolean>(false);
  const { queryState, isLoading, handle } = useDeliveryCompatibleStuff();
  const hasDeliveryCompatibleValidationErrors = queryState.error && (queryState.error as any).status === 422;
  const hasDeliveryAddressForm =
    orderMethod === ORDER_METHODS.DELIVERY &&
    place &&
    (!isSubmitSuccessful || hasDeliveryCompatibleValidationErrors) &&
    !isSubmitting;
  const deliveryCompatibleCompanies = queryState.data;

  const franchiseCompanies = useMemo(() => (data?.companies ?? []).filter(Boolean), [data?.companies]);

  const companies = useMemo<CompanyDistanceInterface[]>(
    () =>
      collect(franchiseCompanies)
        .when(
          orderMethod === ORDER_METHODS.DELIVERY,
          (companies) => companies.where('delivery_schedule.length', '!==', 0),
          (companies) => companies.where('work_schedule.length', '!==', 0),
        )
        .when(orderMethod === ORDER_METHODS.DELIVERY, (companies) =>
          companies.whereIn('id', deliveryCompatibleCompanies ?? []),
        )
        .when(orderMethod === ORDER_METHODS.ROOM_SERVICE, (companies) =>
          companies.filter((company) => company?.has_room_service),
        )
        .when(franchiseCompanies.length > 1, (companies) => companies.where('has_live_orders', true))
        .map((item) => {
          const distance = place?.calculateDistance({
            lat: item?.lat,
            lng: item?.lon,
          });

          return {
            company: item,
            distance,
          };
        })
        .sortBy('distance')
        .toArray(),
    [franchiseCompanies, place, deliveryCompatibleCompanies, orderMethod],
  );

  const onPlaceSelected = async (placeResult: google.maps.places.PlaceResult): Promise<void> => {
    const selectedPlaceResource = new GooglePlaceResource(placeResult);

    deliveryAddressForm.setValue('delivery_street_name', selectedPlaceResource.getStreetName());
    deliveryAddressForm.setValue('delivery_street_number', selectedPlaceResource.getStreetNumber());
    deliveryAddressForm.setValue('delivery_city', selectedPlaceResource.getCity());
    deliveryAddressForm.setValue('delivery_zip_code', selectedPlaceResource.getPostalCode());
    deliveryAddressForm.setValue('delivery_country', selectedPlaceResource.getCountry());
    deliveryAddressForm.setValue('delivery_lat', selectedPlaceResource.getLat());
    deliveryAddressForm.setValue('delivery_lng', selectedPlaceResource.getLng());

    setAddressSearch(selectedPlaceResource.formatAddress());
    setPlace(selectedPlaceResource);

    await handle(selectedPlaceResource);
  };

  const clearAddress = () => {
    deliveryAddressForm.reset({
      delivery_lng: undefined,
      delivery_street_number: '',
      delivery_zip_code: '',
      delivery_country: null,
      delivery_lat: undefined,
      delivery_city: '',
      delivery_street_name: '',
    });
    setAddressSearch('');
    setPlace(null);
    setCompany(null);
    resetError();
  };

  const useCurrentLocation = (): void => {
    if (!navigator.geolocation) {
      alert.error({ description: t('errors.location_unavailable') });

      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const { success, location } = await GooglePlaceResource.getPlaceFromCoordinates({
            lat: coords.latitude,
            lng: coords.longitude,
          });

          if (!success) {
            alert.error({ description: t('errors.location_lookup_failed') });

            return;
          }

          await onPlaceSelected(location);
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocating(false);
        alert.error({ description: t('errors.location_unavailable') });
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  useEffect(() => {
    if (!place) {
      return;
    }

    handle(place);
  }, [place, orderMethod]);

  useEffect(() => {
    async function setInitialPlace(): Promise<void> {
      if (!deliveryAddressForm?.getValues('delivery_lat') || !deliveryAddressForm?.getValues('delivery_lng')) {
        return;
      }

      const { success, location } = await GooglePlaceResource.getPlaceFromCoordinates({
        lat: deliveryAddressForm?.getValues('delivery_lat'),
        lng: deliveryAddressForm?.getValues('delivery_lng'),
      });

      if (!success) {
        return;
      }

      const locationResource = new GooglePlaceResource(location);

      setPlace(locationResource);
      setAddressSearch(locationResource.formatAddress());
    }

    setInitialPlace();
  }, []);

  useEffect(() => {
    if (orderMethod !== ORDER_METHODS.DELIVERY) {
      return;
    }

    if (!place || isSubmitSuccessful) {
      return;
    }

    handle(place);
  }, [orderMethod]);

  const scrollToCompany = (id: number): void => {
    const element = document.getElementById(`company-${id}`);
    if (!element) return;
    element.scrollIntoView({ behavior: 'auto', block: 'start' });
  };

  useLayoutEffect(() => {
    if (!company?.id || companies?.length === 0 || isLoading) return;
    scrollToCompany(company?.id);
  }, [companies, isLoading]);

  const autocompleteBounds = useMemo(
    () => GooglePlaceResource.autocompleteBounds(franchiseCompanies.map(({ lat, lon }) => ({ lat, lng: lon }))),
    [franchiseCompanies],
  );

  const searchPlaceholder = t(
    orderMethod === ORDER_METHODS.DELIVERY ? 'form.address.delivery.placeholder' : 'form.address.placeholder',
  );
  const hasNoDeliveryCompaniesLabel =
    orderMethod === ORDER_METHODS.DELIVERY &&
    !queryState.isUninitialized &&
    !queryState.isLoading &&
    (!queryState?.data?.length || !companies?.length);

  const hasDeliveryCompaniesSearch = useMemo(() => {
    if (data?.has_multiple_companies || orderMethod === ORDER_METHODS.PICKUP || !data?.companies?.length) {
      return true;
    }

    const company = collect(data?.companies).first();

    return company?.has_delivery && company?.delivery_enabled && collect(company?.delivery_zones).isNotEmpty();
  }, [data?.companies, data?.has_multiple_companies, orderMethod]);

  const showsAddressSearch =
    hasDeliveryCompaniesSearch &&
    !(orderMethod === ORDER_METHODS.PICKUP && (!data?.has_multiple_companies || locationSwitchDisabled));

  // Maps loads only once the customer starts using the address search (focus or first keystroke),
  // not merely because the modal opened (it auto-opens on many restaurants). See GoogleMapsLoader.
  const [hasUsedAddressSearch, setHasUsedAddressSearch] = useState<boolean>(false);
  const [isAddressSearchFocused, setIsAddressSearchFocused] = useState<boolean>(false);
  const {
    isLoaded: isGoogleMapsLoaded,
    loadError: googleMapsLoadError,
    retry: retryGoogleMaps,
  } = useGoogleMaps({ enabled: showsAddressSearch && hasUsedAddressSearch });

  const renderSearchBar = (): ReactElement | null => {
    if (!showsAddressSearch) {
      return null;
    }

    return (
      <div className="space-y-xs">
        <div className="flex items-center w-full px-md py-sm bordered space-x-md">
          {isLoading ? (
            <LoadingIndicator className="size-2" size={16} />
          ) : (
            <LocationIcon className="size-4" color={theme.color.brand.icon.color} />
          )}
          {/* Places binds to the input once, on mount, so it must not mount before Maps has loaded.
              Until then a plain input keeps the same value, so nothing typed is lost, and the
              autocomplete takes over the focus when it mounts. */}
          {!isGoogleMapsLoaded ? (
            <input
              data-test="address-search-input"
              onFocus={() => {
                setHasUsedAddressSearch(true);
                setIsAddressSearchFocused(true);
              }}
              onBlur={() => setIsAddressSearchFocused(false)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setHasUsedAddressSearch(true);
                setAddressSearch(e.target.value);
              }}
              value={addressSearch}
              placeholder={searchPlaceholder}
              className="w-full text-sm overflow-ellipsis placeholder:text-[#777777]"
            />
          ) : (
            <AutoComplete
              data-test="address-search-input"
              autoFocus={isAddressSearchFocused}
              onPlaceSelected={onPlaceSelected}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddressSearch(e.target.value)}
              value={addressSearch}
              placeholder={searchPlaceholder}
              className="w-full text-sm overflow-ellipsis placeholder:text-[#777777]"
              options={{
                // Deliberate Google Places API quirk, not a region-data assumption: UK postcodes
                // aren't `address`-typed, so GB needs its own autocomplete type set to match at all.
                types: data?.country === 'GB' ? ['street_address', 'postal_code'] : ['address'],
                ...(autocompleteBounds
                  ? { bounds: autocompleteBounds, strictBounds: false }
                  : data?.country && { componentRestrictions: { country: data.country } }),
              }}
              key={data?.country}
              onError={(e) => console.error('Google place autocomplete error:', e)}
            />
          )}
          {!!addressSearch?.length && (
            <div
              className="bg-gray-700 rounded-full flex flex-none justify-center items-center size-4 cursor-pointer"
              onClick={clearAddress}
            >
              <CloseIcon className="size-2" color={theme.color.gray['500']} />
            </div>
          )}
        </div>
        {googleMapsLoadError && (
          <p data-test="address-search-maps-error" className="text-xs text-brand-text-primary">
            {t('errors.address_suggestions_unavailable')}{' '}
            <button type="button" onClick={retryGoogleMaps} className="font-medium underline">
              {t('buttons.retry')}
            </button>
          </p>
        )}
        <button
          type="button"
          onClick={useCurrentLocation}
          disabled={locating}
          className="flex items-center gap-2xs text-sm font-medium text-brand-primary hover:underline disabled:opacity-50 transition-colors"
        >
          {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
          {locating ? t('buttons.locating') : t('buttons.use_current_location')}
        </button>
      </div>
    );
  };

  const renderError = (): ReactElement | null => {
    // Checked before `hasNoDeliveryCompaniesLabel`: after this error the compatible-companies query
    // is settled with no data, so the generic label is always true and would shadow this copy.
    if (validationError === ValidationErrors.no_couriers_available) {
      return <ErrorLabel message={<NoCouriersErrorContent code={masterPromoCode} />} />;
    }

    if (hasNoDeliveryCompaniesLabel) {
      return <ErrorLabel message={t('errors.no_delivery_companies')} />;
    }

    if (!hasDeliveryCompaniesSearch) {
      return <ErrorLabel message={t('errors.delivery_not_available')} />;
    }

    if (addressValidationError) {
      return <ErrorLabel message={tErrors(`error_messages.${addressValidationError}`)} />;
    }

    return null;
  };

  return (
    <>
      <section className={classnames('space-y-md flex flex-col', className)} {...rest}>
        {renderSearchBar()}
        {renderError()}
        {hasDeliveryAddressForm && <DeliveryAddressForm />}
        {validationError && validationError !== ValidationErrors.no_couriers_available && (
          <p className="text-danger text-sm">{tErrors(`error_messages.${validationError}`)}</p>
        )}
        {!isLoading &&
          companies?.map((item, index) => (
            <div key={`${item.company?.id}${orderMethod}`} id={`company-${item.company?.id}`}>
              <CompanyCard
                data={item}
                className={classnames('pt-md', !!index && 'border-t')}
                showRadioButton={!locationSwitchDisabled}
              />
            </div>
          ))}
      </section>
    </>
  );
}

const errorLabel =
  'bg-[#BC4C6B26]/15 px-sm py-[10px] rounded-lg text-[#BC4C6B] font-semibold text-sm flex items-center gap-x-sm';
